import "./App.css";
import * as React from "react";
import { Admin, Resource, CustomRoutes } from "react-admin";
import { Navigate, Route } from "react-router-dom";
import polyglotI18nProvider from "ra-i18n-polyglot";
import spanishMessages from "./i18n/spanishMessages";
import Geocode from "react-geocode";
import ReactGA from "react-ga4";

import restaurants from "./restaurants";
import categories from "./categories";
import regions from "./regions";
import products from "./products";
import plans from "./plans";
import users from "./users";
import Login from "./login/Login";
import restaurantPlans from "./restaurantPlans";
import coupons from "./coupons";

import authProvider from "./authProvider";
import dataProvider from "./dataProvider";

import { ROLE_TYPES } from "./enums/Roles";

import { theme, darkTheme } from "./common/Theme";
import Layout from "./common/Layout";
import placeTypes from "./placeTypes";
import PlaceTargets from "./placeTargets";
import Districts from "./districts";
import banners from "./banners";
import restaurantsStats from "./restaurantsStats";
import purchases from "./purchases";
import purchasesActivity from "./purchasesActivity";
import restaurantMercadoPagoTokens from "./restaurantMercadoPagoTokens";
import Payments from "./payments/Payments";
import sections from "./sections";
import reviews from "./reviews";
import rewards from "./rewards";
import reports from "./reports";
import RestaurantAudit from "./restaurants/RestaurantAudit";
import ProductAudit from "./products/ProductAudit";
import UserAudit from "./users/UserAudit";
import BannerAudit from "./banners/BannerAudit";
import ProductEventAudit from "./productEvents/ProductEventAudit";
import billing from "./billing";
import videos from "./videos";
import ProductBulkCreate from "./products/ProductBulkCreate";
import discountCoupons from "./discountCoupons";
import ProductDraftBulkCreate from "./productsDraft/ProductDraftBulkCreate";
import Register from "./register/register";
import MultiStepForm from "./autoOnboarding/MultiStepForm";
import { setAuthData } from "./utils/AuthUtils";
import restaurantsDraft from "./restaurantsDraft";
import productsDraft from "./productsDraft";
import { initFacebookPixel } from "./services/MetaTracker";
import communityPosts from "./community";
import productEvents from "./productEvents";

const {
  NODE_ENV,
  REACT_APP_GEOCODE_API_KEY,
  REACT_APP_GTM_ANALYTICS_ID,
  REACT_APP_GTM_ADS_ID,
} = process.env;

/* Geocode API */
Geocode.setApiKey(REACT_APP_GEOCODE_API_KEY);
Geocode.setLocationType("ROOFTOP");
if (NODE_ENV === "development") {
  Geocode.enableDebug();
}

// init Google Tag Manager
ReactGA.initialize([
  {
    trackingId: REACT_APP_GTM_ANALYTICS_ID,
  },
  {
    trackingId: REACT_APP_GTM_ADS_ID,
  },
]);
// init Facebook Pixel
initFacebookPixel();

/* Translations */
const i18nProvider = polyglotI18nProvider(() => spanishMessages, "es");

const App = () => {
  const [loading, setLoading] = React.useState(false);
  const fetchingData = React.useRef(false);

  React.useEffect(() => {
    const main = async () => {
      try {
        if (fetchingData.current) return;
        fetchingData.current = true;
        setLoading(true);

        // check if token is in url by query params ?token=
        const tokenFromUrl = new URLSearchParams(window.location.search).get(
          "token"
        );

        if (!tokenFromUrl) {
          return;
        }

        setAuthData(tokenFromUrl);

        window.location.href = "/";
        return;
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        fetchingData.current = false;
      }
    };
    main();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900 dark:border-white"></div>
      </div>
    );
  }

  return (
    <Admin
      i18nProvider={i18nProvider}
      dataProvider={dataProvider}
      authProvider={authProvider}
      theme={theme}
      darkTheme={darkTheme}
      disableTelemetry
      loginPage={Login}
      layout={Layout}
    >
      {/*
        Every <Resource> is registered for every role. Who reaches what is decided
        in one place: utils/AccessControl.js, which authProvider.canAccess reads.
        react-admin then hides the menu entry (ResourceMenuItem returns null when
        canAccess is false) and blocks the page (useListController ->
        useRequireAccess redirects to /access-denied).

        So adding a page means adding the <Resource> here and one line in
        AccessControl.js — no role conditionals in this file.

        `permissions` is still used below for role-dependent props and labels, and
        to gate the /payments route on being logged in at all.
      */}
      {(permissions) => [
        <CustomRoutes key="routes-root" noLayout>
          <Route exact path="/" element={<Navigate to="/login" />} />
        </CustomRoutes>,
        <CustomRoutes key="routes-public" noLayout>
          <Route exact path="/register" element={<Register />} />
          <Route exact path="/onboarding" element={<MultiStepForm />} />
        </CustomRoutes>,
        permissions && (
          <CustomRoutes key="routes-payments">
            <Route exact path="/payments" element={<Payments />} />
          </CustomRoutes>
        ),

        <Resource
          key="purchases"
          name="purchases"
          {...purchases}
          options={{ label: "Pedidos" }}
        />,

        <Resource
          key="purchases-activity"
          name="purchases/activity"
          {...purchasesActivity}
          options={{ label: "Actividad" }}
        />,

        <Resource
          key="restaurants/stats"
          name="restaurants/stats"
          path="stats"
          match="stats"
          {...restaurantsStats}
          options={{ label: "Estadísticas" }}
        />,

        <Resource
          key="restaurants"
          name="restaurants"
          {...restaurants}
          options={{
            label:
              permissions === ROLE_TYPES.ADMIN ? "Restaurantes" : "Mi Local",
          }}
        >
          <Route path=":id/audits" element={<RestaurantAudit />} />
        </Resource>,
        <Resource
          key="categories"
          name="categories"
          {...categories}
          options={{ label: "Categorías" }}
        />,

        <Resource
          key="regions"
          name="regions"
          {...regions}
          options={{ label: "Regiones" }}
        />,

        <Resource
          key="products"
          name="products"
          {...products}
          options={{ label: "Productos" }}
        >
          <Route path=":id/audits" element={<ProductAudit />} />
          {permissions === ROLE_TYPES.ADMIN && (
            <Route
              path="bulk-create"
              element={<ProductBulkCreate permissions={permissions} />}
            />
          )}
        </Resource>,

        <Resource
          key="users"
          name="users"
          {...users}
          options={{ label: "Usuarios" }}
        >
          <Route path=":id/audits" element={<UserAudit />} />
        </Resource>,

        <Resource
          key="subscription-plans"
          name="subscription-plans"
          {...plans}
          options={{ label: "Usuarios" }}
        />,

        <Resource
          key="restaurant-subscription-plans"
          name="restaurant-subscription-plans"
          {...restaurantPlans}
          options={{ label: "Restaurantes" }}
        />,

        <Resource
          key="coupons"
          name="coupons"
          {...coupons}
          options={{ label: "Canjes" }}
        />,

        <Resource
          key="place-types"
          name="place-types"
          {...placeTypes}
          options={{ label: "Tipos de Lugares" }}
        />,

        <Resource
          key="place-targets"
          name="place-targets"
          {...PlaceTargets}
          options={{ label: "Targets de Lugares" }}
        />,

        <Resource
          key="districts"
          name="districts"
          {...Districts}
          options={{ label: "Barrios" }}
        />,

        <Resource
          key="discount-coupons"
          name="discount-coupons"
          {...discountCoupons}
          options={{ label: "Cupones" }}
        />,

        <Resource
          key="banners"
          name="banners"
          {...banners}
          options={{ label: "Banners" }}
        >
          <Route path=":id/audits" element={<BannerAudit />} />
        </Resource>,

        <Resource
          key="restaurant-mp-tokens"
          name="restaurant-mp-tokens"
          {...restaurantMercadoPagoTokens}
          options={{ label: "Mercado Pago" }}
        />,

        <Resource
          key="sections"
          name="sections"
          {...sections}
          options={{ label: "Secciones" }}
        />,

        <Resource
          key="videos"
          name="videos"
          {...videos}
          options={{ label: "Videos" }}
        />,

        <Resource
          key="community-posts"
          name="community-posts"
          {...communityPosts}
          options={{ label: "Publicaciones" }}
        />,

        <Resource
          key="product-events"
          name="product-events"
          {...productEvents}
          options={{ label: "Eventos" }}
        >
          <Route path=":id/audits" element={<ProductEventAudit />} />
        </Resource>,

        <Resource
          key="reviews"
          name="reviews"
          {...reviews}
          options={{ label: "Calificaciones" }}
        />,

        <Resource
          key="reports"
          name="reports"
          {...reports}
          options={{ label: "Reportes" }}
        />,
        <Resource
          key="billing"
          name="billing"
          {...billing}
          options={{ label: "Facturación" }}
        />,

        <Resource
          key="redeemed-rewards"
          name="redeemed-rewards"
          {...rewards}
          options={{ label: "Premios" }}
        />,

        <Resource
          key="restaurants-draft"
          name="restaurants-draft"
          {...restaurantsDraft}
          options={{ label: "Solicitudes" }}
        />,

        <Resource
          key="products-draft"
          name="products-draft"
          {...productsDraft}
          options={{ label: "Productos" }}
        >
          {permissions === ROLE_TYPES.ADMIN && (
            <Route
              path="bulk-create-draft"
              element={<ProductDraftBulkCreate permissions={permissions} />}
            />
          )}
        </Resource>,
      ]}
    </Admin>
  );
};

export default App;
